<template>
    <div class="pagina-documentos">
        <div class="container">
            <h1 class="titulo">
                📄 LISTA DE DOCUMENTOS PÚBLICOS - CIM-AMREC
            </h1>

            <div class="filtro-grupo">
                <select
                    v-model="grupoSelecionado"
                    class="select-grupo"
                    @change="filtrarPorGrupo"
                >
                    <option
                        v-for="grupo in gruposDisponiveis"
                        :key="grupo"
                        :value="grupo"
                    >
                        {{ grupo }}
                    </option>
                </select>
            </div>

            <div v-if="isLoading" class="loading">
                <span class="spinner"></span>
                Carregando...
            </div>

            <div
                v-for="(arquivos, pasta) in pastasAgrupadas"
                :key="pasta"
                class="pasta"
            >
                <button
                    type="button"
                    class="btn-pasta"
                    @click="togglePasta(pasta)"
                >
                    <span>{{ pasta }}</span>

                    <span class="icone-pasta">
                        {{ pastaAberta[pasta] ? '▲' : '▼' }}
                    </span>
                </button>

                <div
                    v-if="pastaAberta[pasta]"
                    class="arquivos"
                >
                    <div
                        v-for="arquivo in arquivos"
                        :key="arquivo.id"
                        class="card-arquivo"
                    >
                        <div class="info-arquivo">
                            <div class="texto-arquivo">
                                <h3>{{ arquivo.nmdoc }}</h3>
                                <p>{{ arquivo.obspostagem }}</p>
                            </div>

                            <div class="acao-arquivo">
                                <button
                                    type="button"
                                    class="btn-icon"
                                    aria-label="Baixar documento"
                                    @click="visualizarAnexo(arquivo)"
                                >
                                    <img
                                        :src="getIconPath()"
                                        alt="Download"
                                        class="icon-svg"
                                    />
                                </button>

                                <span class="tamanho">
                                    {{ formatarTamanho(arquivo.fsize) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="msgAlerta" class="alerta">
                {{ msgAlerta }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { Api } from '@/funcoes/apiExport';
import { onMounted, ref } from 'vue';

const lsDados = ref([]);
const pastasAgrupadas = ref({});
const pastaAberta = ref({});
const msgAlerta = ref(null);
const isLoading = ref(false);

const grupoSelecionado = ref('');
const gruposDisponiveis = ref([]);

function agruparPorPasta(lista) {
    const agrupado = {};

    lista.forEach((item) => {
        if (!agrupado[item.nmpasta]) {
            agrupado[item.nmpasta] = [];
        }

        agrupado[item.nmpasta].push(item);
    });

    return agrupado;
}

function filtrarPorGrupo() {
    const filtrados = grupoSelecionado.value
        ? lsDados.value.filter(
            (item) => item.nmdrivegrupo === grupoSelecionado.value
        )
        : lsDados.value;

    pastasAgrupadas.value = agruparPorPasta(filtrados);
}

function formatarTamanho(bytes) {
    const tamanho = Number(bytes) || 0;

    if (tamanho < 1024) {
        return `${tamanho} B`;
    }

    if (tamanho < 1024 * 1024) {
        return `${(tamanho / 1024).toFixed(1)} KB`;
    }

    return `${(tamanho / (1024 * 1024)).toFixed(1)} MB`;
}

function getIconPath() {
    return require('@/assets/icons/download.svg');
}

function obterGrupos(lista) {
    const grupos = new Set(
        lista
            .map((item) => item.nmdrivegrupo)
            .filter((grupo) => grupo)
    );

    return Array.from(grupos);
}

function togglePasta(pasta) {
    pastaAberta.value[pasta] = !pastaAberta.value[pasta];
}

async function listarAquivos() {
    try {
        isLoading.value = true;
        msgAlerta.value = null;

        const resposta = await Api.publicPost(
            '/public/drive/arquivos',
            {
                idep: 1,
                ativo: 'S',
                strparam1: 'S',
            }
        );

        lsDados.value = Array.isArray(resposta) ? resposta : [];
        gruposDisponiveis.value = obterGrupos(lsDados.value);
        grupoSelecionado.value = gruposDisponiveis.value[0] || '';

        filtrarPorGrupo();
    } catch (error) {
        console.error(error);

        msgAlerta.value =
            `ATENÇÃO: ${error.response?.data || error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function visualizarAnexo(obj) {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        if (obj.storage_cdtipo === 2) {
            console.log(obj);

            const resposta = await Api.publicPost(
                '/public/solicitar/download',
                {
                    id: obj.idanexo,
                    datainicial: obj.dtanexo
                }
            );

            if (resposta.bolok === 1 && resposta.download_url) {
                window.open(resposta.download_url, '_blank');
            }
        }else{
            msgAlerta.value = 'Opção de armazenamento inválida. Consulte suporte';
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.response?.data || error.message}`;
    } finally {
        isLoading.value = false;
    }
}

onMounted(listarAquivos);
</script>

<style scoped>
/*
 * Esta tela cria sua própria área de rolagem porque o body e o #app
 * possuem overflow: hidden no layout global.
 */
.pagina-documentos {
    width: 100%;
    height: 100vh;
    height: 100dvh;

    overflow-x: hidden;
    overflow-y: auto;

    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.container {
    width: 100%;
    max-width: 900px;
    min-height: 100%;

    margin: 0 auto;
    padding: 20px;

    font-family: sans-serif;
}

.titulo {
    margin: 0 0 40px;
    text-align: center;
}

.filtro-grupo {
    position: relative;
    margin-bottom: 20px;
}

/*
 * A seta é desenhada com gradientes.
 * Assim não depende de imagem externa ou caractere Unicode.
 */
.select-grupo {
    width: 100%;
    min-height: 44px;

    padding: 10px 50px 10px 50px;

    border: none;
    border-radius: 8px;

    background-color: #1d5153;
    background-image:
        linear-gradient(45deg, transparent 50%, white 50%),
        linear-gradient(135deg, white 50%, transparent 50%);
    background-position:
        calc(100% - 21px) 50%,
        calc(100% - 15px) 50%;
    background-size:
        6px 6px,
        6px 6px;
    background-repeat: no-repeat;

    color: white;
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    text-align-last: center;

    cursor: pointer;
    outline: none;

    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.select-grupo:hover {
    background-color: #174547;
}

.select-grupo:focus {
    box-shadow:
        0 0 0 3px rgba(41, 116, 118, 0.25),
        0 2px 4px rgba(0, 0, 0, 0.1);
}

.select-grupo option {
    background-color: white;
    color: #222;
    text-align: left;
}

.loading {
    display: flex;
    align-items: center;
    gap: 10px;

    margin-bottom: 20px;

    color: #005baa;
    font-weight: bold;
}

.spinner {
    width: 20px;
    height: 20px;

    border: 3px solid #ccc;
    border-top-color: #005baa;
    border-radius: 50%;

    animation: spin 1s linear infinite;
}

.pasta {
    margin-bottom: 20px;
}

.btn-pasta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    width: 100%;
    padding: 10px 15px;

    border: none;

    background-color: #297476;
    color: white;

    font-weight: bold;
    text-align: left;

    cursor: pointer;
}

.btn-pasta:hover {
    background-color: #236567;
}

.icone-pasta {
    flex-shrink: 0;
    font-size: 12px;
}

.arquivos {
    margin-top: 10px;
    padding-left: 10px;
}

.card-arquivo {
    margin-bottom: 10px;
    padding: 8px 10px;

    border-radius: 8px;

    background-color: #f4f4f4;

    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-arquivo {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.texto-arquivo {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.texto-arquivo h3 {
    margin: 0;

    color: #222;
    font-size: 16px;
    font-weight: bold;

    overflow-wrap: anywhere;
}

.texto-arquivo p {
    margin: 4px 0 0;

    color: #444;
    font-size: 13px;

    overflow-wrap: anywhere;
}

.acao-arquivo {
    display: flex;
    flex: 0 0 50px;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    width: 50px;
}

.btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    padding: 0;

    border: none;
    background: transparent;

    cursor: pointer;
}

.icon-svg {
    display: block;
    width: 24px;
    height: 24px;
}

.tamanho {
    color: #666;
    font-size: 11px;
    text-align: center;
}

.alerta {
    margin-top: 20px;

    color: red;
    font-weight: bold;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 600px) {
    .container {
        padding: 15px 10px 30px;
    }

    .titulo {
        margin-bottom: 25px;
        font-size: 20px;
    }

    .select-grupo {
        padding-right: 45px;
        padding-left: 15px;
        text-align: left;
        text-align-last: left;
    }

    .arquivos {
        padding-left: 0;
    }
}
</style>